import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigationType } from 'react-router-dom';
import { hapticLight } from '../utils/haptics';

// Cross-fade page navigations via the View Transitions API.
//
// react-router v6 only ships `unstable_viewTransition` for data routers
// (createBrowserRouter); this app mounts a plain <BrowserRouter>, so we
// drive the API by hand instead: keep a trailing copy of the location
// and swap it inside document.startViewTransition, with
// <Routes location={...}> rendering the trailing copy. The browser
// snapshots the old DOM, we commit the new route synchronously, and it
// cross-fades between the two. Animation timing lives in index.css
// under the ::view-transition-* selectors.
//
// The transition is gated to PATHNAME changes only — search-param
// updates (filter modal URL sync, profile ?tab=) re-render in place,
// and a full-page fade on every filter toggle would read as flicker,
// not polish. Unsupported browsers and prefers-reduced-motion fall
// through to an instant swap, same as before this hook existed.
export default function useViewTransitionLocation() {
  const location = useLocation();
  const navType = useNavigationType();
  const [displayedLocation, setDisplayedLocation] = useState(location);

  useEffect(() => {
    if (location === displayedLocation) return;
    const samePath = location.pathname === displayedLocation.pathname;

    // A real page change (not a same-path search-param sync) earns a subtle
    // native tick. No-ops on web.
    if (!samePath) hapticLight();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (samePath || reducedMotion || typeof document.startViewTransition !== 'function') {
      setDisplayedLocation(location);
      return;
    }

    // Tell the CSS which way to slide: POP (back button / iOS edge-swipe)
    // slides the page back toward the right; PUSH/REPLACE slide forward in
    // from the right. Set on <html> right before the swap — harmless to leave
    // between navigations since the ::view-transition pseudo-elements only
    // exist mid-transition.
    document.documentElement.dataset.nav = navType === 'POP' ? 'back' : 'forward';

    const transition = document.startViewTransition(() => {
      // flushSync so the new route is fully committed inside the
      // transition callback — React would otherwise batch the update
      // past the browser's snapshot and it would animate old → old.
      flushSync(() => {
        setDisplayedLocation(location);
      });
    });
    // A skipped transition is benign: the update callback (our setState) still
    // runs, so the route swaps — only the animation is dropped. `.ready` (and
    // only `.ready`; `.finished`/`.updateCallbackDone` still resolve) rejects on
    // two skip causes, both swallowed here:
    //   - AbortError: a rapid follow-up navigation superseded this transition
    //     (e.g. the /salon/dashboard role guard's <Navigate to="/" replace />
    //     firing right after login).
    //   - InvalidStateError: the document was hidden / not fully active when the
    //     transition started — on iOS the app can navigate while backgrounded,
    //     and WebKit skips with "View transition was skipped because document
    //     visibility state is hidden" (Sentry petzy-business).
    // Re-throw anything else so genuine failures still surface.
    //
    // Keyed on the spec-stable DOMException NAME, NOT the message — the message
    // text is engine-specific (Chromium: "Transition was skipped"; Safari/WebKit:
    // "Old view transition aborted by new view transition."), so matching on it
    // would silence only one browser. Both engines follow the spec's "skip the
    // view transition" step here, so these two names cover Chromium and Safari
    // alike — do not "fix" this by message-matching.
    transition.ready.catch((err) => {
      if (err?.name !== 'AbortError' && err?.name !== 'InvalidStateError') throw err;
    });
  }, [location, displayedLocation, navType]);

  return displayedLocation;
}
